import type { WatchedRange } from '@kidshub/contracts';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { formatClock } from '../lib/duration.js';
import type { Coverage } from './coverage.js';
import { BUCKET_SECONDS, isRewatch } from './coverage.js';

/**
 * The charts on the watching screens, drawn as plain SVG on the design tokens.
 *
 * Amber is time — the tokens' own rule — so minutes are amber. Counts and lines
 * are ink. Clay marks only where a child left, and never without a label beside
 * it. Every chart has a hover read-out, because amber on paper is a shade under
 * 3:1 and a value a parent cannot read off the axis must be readable somewhere.
 */

/** The width a chart has to draw in, followed as the layout changes. */
const useWidth = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (element === null) {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      setWidth(Math.round(entry?.contentRect.width ?? 0));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { ref, width };
};

interface Tip {
  x: number;
  y: number;
  content: ReactNode;
}

const Tooltip = ({ tip }: { tip: Tip | null }) =>
  tip === null ? null : (
    <div className="wa-tip" style={{ left: tip.x, top: tip.y }} role="status">
      {tip.content}
    </div>
  );

/** A column's rounded top, square at the baseline. */
const columnPath = (x: number, top: number, width: number, baseline: number) => {
  const radius = Math.min(4, (baseline - top) / 2, width / 2);
  return `M${x},${baseline} V${top + radius} q0,-${radius} ${radius},-${radius} H${x + width - radius} q${radius},0 ${radius},${radius} V${baseline} Z`;
};

export interface DailyBar {
  label: string;
  value: number;
  tip: ReactNode;
}

/** Minutes per day, with the daily limit as a line. */
export const DailyChart = ({
  bars,
  limit,
  limitLabel,
  label,
}: {
  bars: DailyBar[];
  limit: number;
  limitLabel: string;
  label: string;
}) => {
  const { ref, width } = useWidth();
  const [tip, setTip] = useState<Tip | null>(null);

  const height = 210;
  const top = 22;
  const bottom = 26;
  const left = 8;
  const right = 72;
  const plotWidth = Math.max(0, width - left - right);
  const plotHeight = height - top - bottom;
  const max = Math.max(limit, ...bars.map((bar) => bar.value), 1) * 1.1;
  const band = bars.length === 0 ? 0 : plotWidth / bars.length;
  const barWidth = Math.min(24, band * 0.6);
  const y = (value: number) => top + plotHeight - (value / max) * plotHeight;
  const labelEvery = bars.length > 10 ? 5 : 1;

  return (
    <div className="wa-chart" ref={ref}>
      {width === 0 ? null : (
        <svg width={width} height={height} role="img" aria-label={label}>
          <line className="wa-grid-line" x1={left} x2={width - right} y1={y(0)} y2={y(0)} />
          {bars.map((bar, index) => {
            const x = left + band * index + (band - barWidth) / 2;
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: one column per day, in order
              <g key={index}>
                {bar.value > 0 ? (
                  <path
                    d={columnPath(x, y(bar.value), barWidth, y(0))}
                    fill="var(--kh-color-amber)"
                  />
                ) : null}
                {bars.length <= 10 && bar.value > 0 ? (
                  <text
                    className="wa-value"
                    x={x + barWidth / 2}
                    y={y(bar.value) - 6}
                    textAnchor="middle"
                  >
                    {bar.value}
                  </text>
                ) : null}
                {index % labelEvery === 0 || index === bars.length - 1 ? (
                  <text
                    className="wa-label"
                    x={x + barWidth / 2}
                    y={height - 6}
                    textAnchor="middle"
                  >
                    {bar.label}
                  </text>
                ) : null}
                <rect
                  className="wa-hit"
                  x={left + band * index}
                  y={top}
                  width={band}
                  height={plotHeight}
                  onPointerEnter={() =>
                    setTip({ x: x + barWidth / 2, y: y(bar.value), content: bar.tip })
                  }
                  onPointerLeave={() => setTip(null)}
                />
              </g>
            );
          })}
          {limit > 0 ? (
            <>
              <line
                x1={left}
                x2={width - right + 4}
                y1={y(limit)}
                y2={y(limit)}
                stroke="var(--kh-color-ink-2)"
                strokeWidth={1.5}
              />
              <text className="wa-label" x={width - right + 8} y={y(limit) + 4}>
                {limitLabel}
              </text>
            </>
          ) : null}
        </svg>
      )}
      <Tooltip tip={tip} />
    </div>
  );
};

/** Minutes by local day and hour, as a five-step amber ramp. */
export const HourHeat = ({
  rows,
  firstHour,
  lastHour,
  cellTitle,
}: {
  rows: { label: string; seconds: number[] }[];
  firstHour: number;
  lastHour: number;
  cellTitle: (rowLabel: string, hour: number, seconds: number) => string;
}) => {
  const hours = Array.from({ length: lastHour - firstHour + 1 }, (_, index) => firstHour + index);
  const max = Math.max(1, ...rows.flatMap((row) => row.seconds));
  const level = (seconds: number) =>
    seconds === 0 ? 0 : Math.min(4, Math.ceil((seconds / max) * 4));

  return (
    <div
      className="wa-heat"
      style={{ gridTemplateColumns: `40px repeat(${hours.length}, minmax(0, 1fr))` }}
    >
      <span />
      {hours.map((hour) => (
        <span key={hour} className="wa-heat-h">
          {(hour - firstHour) % 2 === 0 ? String(hour).padStart(2, '0') : ''}
        </span>
      ))}
      {rows.map((row) => (
        <HeatRow key={row.label} row={row} hours={hours} level={level} cellTitle={cellTitle} />
      ))}
    </div>
  );
};

const HeatRow = ({
  row,
  hours,
  level,
  cellTitle,
}: {
  row: { label: string; seconds: number[] };
  hours: number[];
  level: (seconds: number) => number;
  cellTitle: (rowLabel: string, hour: number, seconds: number) => string;
}) => (
  <>
    <span>{row.label}</span>
    {hours.map((hour, index) => {
      const seconds = row.seconds[index] ?? 0;
      return (
        <i
          key={hour}
          className={`wa-h${level(seconds)}`}
          title={cellTitle(row.label, hour, seconds)}
        />
      );
    })}
  </>
);

/** How many times each moment of one video was on screen, and where she left. */
export const CoverageChart = ({
  data,
  durationSeconds,
  label,
  tipFor,
}: {
  data: Coverage;
  durationSeconds: number;
  label: string;
  tipFor: (seconds: number, count: number, exits: number) => ReactNode;
}) => {
  const { ref, width } = useWidth();
  const [cursor, setCursor] = useState<number | null>(null);

  const height = 230;
  const top = 16;
  const bottom = 52;
  const left = 28;
  const right = 8;
  const plotWidth = Math.max(0, width - left - right);
  const plotHeight = height - top - bottom;
  const max = Math.max(...data.counts, 1) + 1;
  const x = (seconds: number) => left + (seconds / durationSeconds) * plotWidth;
  const y = (value: number) => top + plotHeight - (value / max) * plotHeight;

  let line = '';
  let area = `M${x(0)},${y(0)}`;
  data.counts.forEach((count, bucket) => {
    const from = x(bucket * BUCKET_SECONDS);
    const to = x(Math.min(durationSeconds, (bucket + 1) * BUCKET_SECONDS));
    line += `${bucket === 0 ? 'M' : 'L'}${from},${y(count)} L${to},${y(count)} `;
    area += ` L${from},${y(count)} L${to},${y(count)}`;
  });
  area += ` L${x(durationSeconds)},${y(0)} Z`;

  const step = durationSeconds > 900 ? 300 : 120;
  const ticks = Array.from({ length: Math.floor(durationSeconds / step) + 1 }, (_, i) => i * step);
  const gridStep = max > 10 ? 5 : 2;
  const exitY = y(0) + 14;
  const bucket = cursor === null ? null : Math.floor(cursor / BUCKET_SECONDS);

  return (
    <div className="wa-chart" ref={ref}>
      {width === 0 ? null : (
        <svg
          width={width}
          height={height}
          role="img"
          aria-label={label}
          onPointerMove={(event) => {
            const box = event.currentTarget.getBoundingClientRect();
            const seconds = ((event.clientX - box.left - left) / plotWidth) * durationSeconds;
            setCursor(
              seconds < 0 || seconds > durationSeconds
                ? null
                : Math.min(durationSeconds - 1, seconds),
            );
          }}
          onPointerLeave={() => setCursor(null)}
        >
          {data.rewatched === null ? null : (
            <rect
              x={x(data.rewatched.from)}
              y={top}
              width={x(data.rewatched.to) - x(data.rewatched.from)}
              height={plotHeight}
              fill="var(--kh-color-amber-wash)"
            />
          )}
          {Array.from({ length: Math.floor(max / gridStep) + 1 }, (_, i) => i * gridStep).map(
            (value) => (
              <g key={value}>
                <line
                  className="wa-grid-line"
                  x1={left}
                  x2={width - right}
                  y1={y(value)}
                  y2={y(value)}
                />
                <text className="wa-label" x={left - 8} y={y(value) + 4} textAnchor="end">
                  {value}
                </text>
              </g>
            ),
          )}
          <path d={area} fill="var(--kh-color-ink)" fillOpacity={0.1} />
          <path
            d={line}
            fill="none"
            stroke="var(--kh-color-ink)"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {data.exits.map((exits, index) => {
            if (exits === 0) {
              return null;
            }
            const center = x(index * BUCKET_SECONDS + BUCKET_SECONDS / 2);
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: one mark per time slice
              <g key={index}>
                <path
                  d={`M${center - 6},${exitY} L${center + 6},${exitY} L${center},${exitY + 9} Z`}
                  fill="var(--kh-color-clay)"
                />
                {exits > 1 ? (
                  <text className="wa-value" x={center} y={exitY + 22} textAnchor="middle">
                    {exits}
                  </text>
                ) : null}
              </g>
            );
          })}
          {ticks.map((tick) => (
            <text key={tick} className="wa-label" x={x(tick)} y={height - 4} textAnchor="middle">
              {formatClock(tick)}
            </text>
          ))}
          {cursor === null ? null : (
            <line
              x1={x(cursor)}
              x2={x(cursor)}
              y1={top}
              y2={y(0)}
              stroke="var(--kh-color-ink-2)"
              strokeWidth={1}
            />
          )}
        </svg>
      )}
      {cursor === null || bucket === null ? null : (
        <Tooltip
          tip={{
            x: x(cursor),
            y: y(data.counts[bucket] ?? 0),
            content: tipFor(
              bucket * BUCKET_SECONDS,
              data.counts[bucket] ?? 0,
              data.exits[bucket] ?? 0,
            ),
          }}
        />
      )}
    </div>
  );
};

/** One viewing as a strip: what was watched, what again, where it stopped. */
export const ViewTrack = ({
  ranges,
  durationSeconds,
  exitAt,
}: {
  ranges: WatchedRange[];
  durationSeconds: number;
  exitAt: number | null;
}) => {
  const percent = (seconds: number) => `${Math.min(100, (seconds / durationSeconds) * 100)}%`;
  return (
    <div className="wa-track" aria-hidden="true">
      {ranges.map(([from, to], index) => (
        <i
          // biome-ignore lint/suspicious/noArrayIndexKey: stretches in the order they happened
          key={index}
          className={isRewatch(ranges, index) ? 'wa-seg again' : 'wa-seg'}
          style={{ left: percent(from), width: percent(to - from) }}
        />
      ))}
      {exitAt === null ? null : <i className="wa-exit" style={{ left: percent(exitAt) }} />}
    </div>
  );
};
